import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import medicoVfpService, { PacienteVfp } from '../../services/medicoVfpService';
import { recetasService, Receta } from '../../services/recetasService';
import { ArrowLeft as ArrowLeftIcon, Printer as PrinterIcon, FileText as FileTextIcon, History as HistoryIcon, RotateCw as RotateCwIcon, Trash2 as Trash2Icon, Pencil as PencilIcon, Save as SaveIcon, X as XIcon, ClipboardList as ClipboardListIcon } from 'lucide-react';

const PERCENTIL_LABELS: Record<string, string> = {
  muy_bajo: 'Muy bajo (< P3)',
  bajo: 'Bajo (P3-P15)',
  normal_bajo: 'Normal bajo (P15-P50)',
  normal: 'Normal (P50-P85)',
  alto: 'Alto (P85-P97)',
  muy_alto: 'Muy alto (> P97)',
};

function formatMexicoCityDate(now: Date) {
  const tz = 'America/Mexico_City';
  const parts = new Intl.DateTimeFormat('es-MX', {
    timeZone: tz,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const month = get('month').replace('.', '').toUpperCase();
  return `${get('day')} ${month} ${get('year')}`;
}

function formatDayMonthUpper(now: Date) {
  const tz = 'America/Mexico_City';
  const parts = new Intl.DateTimeFormat('es-MX', {
    timeZone: tz,
    day: '2-digit',
    month: 'long',
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')} ${get('month').toUpperCase()}`;
}

function formatDayMonthLower(now: Date) {
  const tz = 'America/Mexico_City';
  const parts = new Intl.DateTimeFormat('es-MX', {
    timeZone: tz,
    day: 'numeric',
    month: 'long',
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')} de ${get('month')}`;
}

const ORDEN_PLANTILLAS: Record<string, () => string> = {
  'corta-estancia': () =>
    `BENEFICENCIA ESPAÑOLA.\n\n` +
    `1. Ingresa a CIRUGIA CORTA ESTANCIA\n` +
    `2. Presentarse en Admisión 7:30 am con ayuno a partir de las 12 de la noche.\n` +
    `3. AHNO\n` +
    `4. Se opera a las 8:30 hrs del ${formatDayMonthUpper(new Date())} en CIRUGIA CORTA ESTANCIA.\n\n` +
    `PAQUETE . NO PEDIR DEPOSITO.`,
  'pediatria': () =>
    `1. Ingresa a PEDIATRIA.\n` +
    `2. Presentarse EN ADMISIÓN a las 6:30hrs\n` +
    `3. Se opera a las 7:00 hs del ${formatDayMonthLower(new Date())}\n` +
    `4. Ayuno a partir de las 12 de la noche del día anterior\n` +
    `5. Reportar ingreso 0442222606190`,
};

function formatMexicoCityTime(now: Date) {
  const tz = 'America/Mexico_City';
  const parts = new Intl.DateTimeFormat('es-MX', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('hour')}:${get('minute')}`;
}

function calcularEdadDetallada(nacimiento?: Date | string | null) {
  if (!nacimiento) return { anios: 0, meses: 0, dias: 0 };
  const birth = new Date(nacimiento);
  if (Number.isNaN(birth.getTime())) return { anios: 0, meses: 0, dias: 0 };

  const today = new Date();
  let y = today.getFullYear() - birth.getFullYear();
  let m = today.getMonth() - birth.getMonth();
  let d = today.getDate() - birth.getDate();

  if (d < 0) {
    // borrow days from previous month
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    d += prevMonth.getDate();
    m -= 1;
  }
  if (m < 0) {
    m += 12;
    y -= 1;
  }
  return { anios: Math.max(0, y), meses: Math.max(0, m), dias: Math.max(0, d) };
}

export default function MedicoVfpRecetaPage() {
  const navigate = useNavigate();
  const location = useLocation() as {
    state?: { restoreOrden?: boolean; ordenTexto?: string; ordenFormat?: string };
  };
  const { clave } = useParams<{ clave: string }>();
  const [loading, setLoading] = useState(true);
  const [paciente, setPaciente] = useState<PacienteVfp | null>(null);
  const [historialEditable, setHistorialEditable] = useState('');
  const historialRef = useRef<HTMLTextAreaElement | null>(null);
  // Texto editable de la consulta actual que se agregará al historial
  const [consultaTexto, setConsultaTexto] = useState('');
  // Texto de receta, solo para impresión (no se guarda en historial)
  const [recetaTexto, setRecetaTexto] = useState('');
  // Orden de ingreso a cirugía (solo impresión, no se guarda como histórico)
  const [ordenTexto, setOrdenTexto] = useState<string>(location.state?.ordenTexto ?? '');
  const [ordenPlantilla, setOrdenPlantilla] = useState<string>('');
  const [ordenFormat, setOrdenFormat] = useState<'beneficencia' | 'angeles'>(() => {
    const s = location.state?.ordenFormat;
    if (s === 'beneficencia' || s === 'angeles') return s;
    const saved = localStorage.getItem('ordenFormat');
    return (saved === 'beneficencia' || saved === 'angeles') ? saved : 'beneficencia';
  });
  const [activeTab, setActiveTab] = useState<'nueva' | 'previas' | 'orden'>(
    location.state?.restoreOrden ? 'orden' : 'nueva'
  );
  const [recetasPrevias, setRecetasPrevias] = useState<Receta[]>([]);
  const [loadingRecetas, setLoadingRecetas] = useState(false);
  const [editingReceta, setEditingReceta] = useState<Receta | null>(null);
  // Separador fijo (requerimiento): 113 guiones (reducido 25% desde 151)
  const FIXED_SEPARATOR = `\n${'-'.repeat(113)}\n`;
  
  const [recipeFormat, setRecipeFormat] = useState<'beneficencia' | 'angeles'>(() => {
    const saved = localStorage.getItem('recipeFormat');
    return (saved === 'beneficencia' || saved === 'angeles') ? saved : 'beneficencia';
  });

  // Límites para la receta (basado en espacio físico de impresión)
  const MAX_RECETA_LINES_BENEFICENCIA = 21;
  const MAX_RECETA_LINES_ANGELES = 21;
  const WARNING_RECETA_LINES_BENEFICENCIA = 19;
  const WARNING_RECETA_LINES_ANGELES = 19;
  const maxRecetaLines = recipeFormat === 'angeles' ? MAX_RECETA_LINES_ANGELES : MAX_RECETA_LINES_BENEFICENCIA;
  const warningRecetaLines = recipeFormat === 'angeles' ? WARNING_RECETA_LINES_ANGELES : WARNING_RECETA_LINES_BENEFICENCIA;

  const loadRecetasPrevias = useCallback(async () => {
    if (!clave) return;
    try {
      setLoadingRecetas(true);
      const recetas = await recetasService.findByPaciente(Number(clave));
      setRecetasPrevias(recetas);
    } catch (e) {
      console.error('Error al cargar recetas previas:', e);
    } finally {
      setLoadingRecetas(false);
    }
  }, [clave]);

  useEffect(() => {
    if (!clave) return;
    const run = async () => {
      try {
        setLoading(true);
        const data = await medicoVfpService.getPacienteVfp(Number(clave));
        setPaciente(data);
        setHistorialEditable(data.historial ?? '');
        loadRecetasPrevias();
      } catch (err) {
        console.error(err);
        toast.error('Error al cargar paciente');
        navigate('/medico-vfp/pacientes');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [clave, navigate, loadRecetasPrevias]);

  useEffect(() => {
    // Al cargar o actualizar historial, posiciona el scroll al final para ver la última entrada
    if (historialRef.current) {
      historialRef.current.scrollTop = historialRef.current.scrollHeight;
    }
  }, [historialEditable]);

  const now = useMemo(() => new Date(), []);
  const fechaMx = useMemo(() => formatMexicoCityDate(now), [now]);
  const horaMx = useMemo(() => formatMexicoCityTime(now), [now]);
  const edad = useMemo(() => calcularEdadDetallada(paciente?.nacimiento ?? null), [paciente]);

  const encabezadoConsulta = useMemo(() => {
    const pesoTxt = paciente?.peso != null ? String(paciente.peso) : '';
    const tallaTxt = paciente?.talla != null ? String(paciente.talla) : '';
    const tempTxt = paciente?.temp != null ? String(paciente.temp) : '';
    const taTxt = paciente?.ta != null ? String(paciente.ta) : '';

    // Texto compacto: solo 3 espacios entre cada dato
    return (
      `Fecha de Consulta : ${fechaMx}   Hora : ${horaMx}   ` +
      `Edad : ${edad.anios} Años ${edad.meses} Meses ${edad.dias} Dias\n` +
      `Peso : ${pesoTxt} Kgs   Talla : ${tallaTxt} Mts   ` +
      `Temp. : ${tempTxt} oC   TA: ${taTxt}\n` +
      `PADECIMIENTO ACTUAL : \n` +
      `EXPLORACION FISICA : \nIMPRESION DIAGNOSTICA : ` +
      `\nTRATAMIENTO  : `
    );
  }, [fechaMx, horaMx, edad, paciente]);

  useEffect(() => {
    if (paciente && !consultaTexto && encabezadoConsulta) {
      // Si es un nuevo paciente (historial vacío), agregar ORIGINARIO DE, separador y ANTECEDENTES
      const esNuevoPaciente = !historialEditable || historialEditable.trim() === '';
      let nuevoTexto = encabezadoConsulta;

      if (esNuevoPaciente) {
        // Agregar ANTECEDENTES antes de EXPLORACION FISICA
        nuevoTexto = nuevoTexto.replace(
          'EXPLORACION FISICA :',
          'ANTECEDENTES :\nEXPLORACION FISICA :'
        );
        // Agregar ORIGINARIO DE y separador al inicio
        nuevoTexto = `ORIGINARIO DE :\n${'-'.repeat(150)}\n${nuevoTexto}`;
      }

      setConsultaTexto(nuevoTexto);
    }
  }, [paciente, encabezadoConsulta, consultaTexto, historialEditable]);

  const extractNumber = (re: RegExp, text: string): number | undefined => {
    const m = re.exec(text);
    if (!m) return undefined;
    const num = Number(m[1].replace(/,/g, '.'));
    return Number.isFinite(num) ? num : undefined;
  };

  // Calcular número de líneas en la receta
  const recetaLines = useMemo(() => {
    if (!recetaTexto) return 0;
    return recetaTexto.split('\n').length;
  }, [recetaTexto]);

  // Determinar el estado del límite de la receta
  const recetaStatus = useMemo(() => {
    if (recetaLines > maxRecetaLines) {
      return { type: 'error', message: `⚠️ Excede el espacio (${recetaLines}/${maxRecetaLines} líneas)` };
    }
    if (recetaLines >= warningRecetaLines) {
      return { type: 'warning', message: `⚡ Cerca del límite (${recetaLines}/${maxRecetaLines} líneas)` };
    }
    return { type: 'ok', message: `✓ ${recetaLines}/${maxRecetaLines} líneas` };
  }, [recetaLines, maxRecetaLines, warningRecetaLines]);

  // Estado del límite para la orden de ingreso (mismo espacio físico de impresión)
  const ordenLines = useMemo(() => {
    if (!ordenTexto) return 0;
    return ordenTexto.split('\n').length;
  }, [ordenTexto]);

  const ordenStatus = useMemo(() => {
    if (ordenLines > maxRecetaLines) {
      return { type: 'error', message: `⚠️ Excede el espacio (${ordenLines}/${maxRecetaLines} líneas)` };
    }
    if (ordenLines >= warningRecetaLines) {
      return { type: 'warning', message: `⚡ Cerca del límite (${ordenLines}/${maxRecetaLines} líneas)` };
    }
    return { type: 'ok', message: `✓ ${ordenLines}/${maxRecetaLines} líneas` };
  }, [ordenLines, maxRecetaLines, warningRecetaLines]);

  const handlePlantillaChange = (value: string) => {
    if (!value) {
      setOrdenPlantilla('');
      return;
    }
    if (ordenTexto.trim()) {
      const continuar = window.confirm('El texto actual de la orden se reemplazará por la plantilla seleccionada. ¿Desea continuar?');
      if (!continuar) return;
    }
    const builder = ORDEN_PLANTILLAS[value];
    if (builder) {
      setOrdenPlantilla(value);
      setOrdenTexto(builder());
    }
  };

  const handleImprimirOrden = () => {
    if (!paciente) return;
    if (!ordenTexto.trim()) {
      toast.error('Capture el texto de la orden o seleccione una plantilla');
      return;
    }
    if (ordenLines > maxRecetaLines) {
      const confirmar = window.confirm(
        `⚠️ ADVERTENCIA: La orden tiene ${ordenLines} líneas, pero solo caben ${maxRecetaLines} líneas en la impresión física.\n\n` +
        `El texto excedente NO se imprimirá correctamente.\n\n` +
        `¿Desea continuar de todas formas?`
      );
      if (!confirmar) return;
    }
    navigate(`/medico-vfp/${paciente.clave}/orden/print`, {
      state: {
        ordenTexto,
        formato: ordenFormat,
      },
    });
  };

  const handleGuardarReceta = async () => {
    if (!paciente) return;
    
    // Advertir si la receta excede el espacio disponible
    if (recetaLines > maxRecetaLines) {
      const confirmar = window.confirm(
        `⚠️ ADVERTENCIA: La receta tiene ${recetaLines} líneas, pero solo caben ${maxRecetaLines} líneas en la impresión física.\n\n` +
        `El texto excedente NO se imprimirá correctamente.\n\n` +
        `¿Desea continuar de todas formas?`
      );
      if (!confirmar) return;
    }
    
    try {
      const bloqueConsulta = consultaTexto.trimEnd();

      // Limpiar espacios extra y crear el historial concatenado
      const historialLimpio = historialEditable ? historialEditable.trim() : '';
      const nuevoHistorial = historialLimpio
        ? `${historialLimpio}${FIXED_SEPARATOR}${bloqueConsulta.trim()}`
        : bloqueConsulta.trim();

      // Parseo de signos vitales a partir del texto
      const pesoNum = extractNumber(/Peso\s*:\s*([0-9]+(?:[\.,][0-9]+)?)/i, bloqueConsulta);
      const tallaNum = extractNumber(/Talla\s*:\s*([0-9]+(?:[\.,][0-9]+)?)/i, bloqueConsulta);
      const tempNum = extractNumber(/Temp\.?\s*:\s*([0-9]+(?:[\.,][0-9]+)?)/i, bloqueConsulta);
      // Asegura coincidir con la palabra completa 'TA' y no con 'Talla'
      const taNum = extractNumber(/\bTA\b\s*:?\s*([0-9]+(?:[\.,][0-9]+)?)/i, bloqueConsulta);

      await medicoVfpService.updatePacienteVfp(paciente.clave, {
        historial: nuevoHistorial,
        ...(pesoNum != null ? { peso: pesoNum } : {}),
        ...(tallaNum != null ? { talla: tallaNum } : {}),
        ...(tempNum != null ? { temp: tempNum } : {}),
        ...(taNum != null ? { ta: taNum } : {}),
      });

      // Guardar la receta en la BD con los datos del paciente en ese momento
      let savedRecetaToken: string | null = null;
      try {
        const edadTxt = `${edad.anios} años ${edad.meses} meses ${edad.dias} días`;
        let percentilePeso = '';
        let percentileTalla = '';
        if (paciente.sexo && (paciente.peso != null || paciente.talla != null)) {
          try {
            const pctResult = await medicoVfpService.calcularPercentiles({
              sexo: paciente.sexo,
              anos: edad.anios,
              meses: edad.meses,
              peso: paciente.peso ?? undefined,
              talla: paciente.talla ?? undefined,
            });
            percentilePeso = pctResult.peso ? (PERCENTIL_LABELS[pctResult.peso] || '') : '';
            percentileTalla = pctResult.talla ? (PERCENTIL_LABELS[pctResult.talla] || '') : '';
          } catch (e) {
            console.error('Error al calcular percentiles para receta:', e);
          }
        }
        const recetaCreada = await recetasService.create({
          pacienteId: paciente.clave,
          nombrePaciente: `${paciente.apellido1 ?? ''} ${paciente.apellido2 ?? ''} ${paciente.nombre ?? ''}`.trim(),
          fechaNacimiento: paciente.nacimiento ? new Date(paciente.nacimiento).toISOString().split('T')[0] : undefined,
          peso: paciente.peso ?? undefined,
          talla: paciente.talla ?? undefined,
          percentilePeso: percentilePeso || undefined,
          percentileTalla: percentileTalla || undefined,
          edadTexto: edadTxt,
          medicamentos: recetaTexto,
          formato: recipeFormat,
        });
        savedRecetaToken = recetaCreada.token;
        loadRecetasPrevias();
      } catch (e) {
        console.error('Error al guardar receta en BD:', e);
      }

      // Navegar a la pantalla de impresión con el contenido de RECETA
      navigate(`/medico-vfp/${paciente.clave}/receta/print`, {
        state: {
          prescription: recetaTexto,
          recetaToken: savedRecetaToken,
          formato: recipeFormat,
        },
      });
      setHistorialEditable(nuevoHistorial);
    } catch (error) {
      console.error('Error al guardar receta:', error);
      toast.error('No se pudo guardar la receta');
    }
  };

  const handleEditarReceta = (receta: Receta) => {
    if (recetaTexto.trim() && !editingReceta) {
      const continuar = window.confirm('El texto actual de la receta se descartará. ¿Desea continuar?');
      if (!continuar) return;
    }
    setEditingReceta(receta);
    setRecetaTexto(receta.medicamentos || '');
    if (receta.formato === 'beneficencia' || receta.formato === 'angeles') {
      setRecipeFormat(receta.formato);
    }
    setActiveTab('nueva');
  };

  const handleCancelarEdicion = () => {
    setEditingReceta(null);
    setRecetaTexto('');
  };

  const handleGuardarEdicion = async (imprimir: boolean) => {
    if (!editingReceta || !paciente) return;

    if (imprimir && recetaLines > maxRecetaLines) {
      const confirmar = window.confirm(
        `⚠️ ADVERTENCIA: La receta tiene ${recetaLines} líneas, pero solo caben ${maxRecetaLines} líneas en la impresión física.\n\n` +
        `El texto excedente NO se imprimirá correctamente.\n\n` +
        `¿Desea continuar de todas formas?`
      );
      if (!confirmar) return;
    }

    try {
      const recetaActualizada = await recetasService.update(editingReceta.id, {
        medicamentos: recetaTexto,
        formato: recipeFormat,
      });
      toast.success('Receta actualizada');
      loadRecetasPrevias();

      if (imprimir) {
        const edadParts = recetaActualizada.edadTexto?.match(/(\d+)\s*años?\s*(\d+)\s*meses?\s*(\d+)\s*d[ií]as?/i) || [];
        navigate(`/medico-vfp/${paciente.clave}/receta/print`, {
          state: {
            prescription: recetaActualizada.medicamentos || '',
            weight: recetaActualizada.peso != null ? String(recetaActualizada.peso) : '',
            height: recetaActualizada.talla != null ? String(recetaActualizada.talla) : '',
            ageYears: edadParts[1] || '',
            ageMonths: edadParts[2] || '',
            ageDays: edadParts[3] || '',
            date: formatMexicoCityDate(new Date(recetaActualizada.fecha)),
            weightPercentile: recetaActualizada.percentilePeso || '',
            heightPercentile: recetaActualizada.percentileTalla || '',
            recetaToken: recetaActualizada.token,
            formato: recetaActualizada.formato || 'beneficencia',
          },
        });
      }
    } catch (e) {
      console.error('Error al actualizar receta:', e);
      toast.error('No se pudo actualizar la receta');
    }
  };

  const handleBorrarReceta = async (id: number) => {
    if (!window.confirm('¿Está seguro de borrar esta receta? Esta acción no se puede deshacer.')) return;
    try {
      await recetasService.remove(id);
      toast.success('Receta borrada');
      loadRecetasPrevias();
    } catch (e) {
      console.error('Error al borrar receta:', e);
      toast.error('No se pudo borrar la receta');
    }
  };

  const handleTabKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = textarea.value.substring(0, start) + '\t' + textarea.value.substring(end);
      textarea.value = newValue;
      textarea.selectionStart = textarea.selectionEnd = start + 1;
      // Trigger React's onChange
      const event = new Event('input', { bubbles: true });
      textarea.dispatchEvent(event);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!paciente) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow sticky top-0 z-20">
        <div className="max-w-full mx-auto px-2 sm:px-3 lg:px-4 py-2">
          <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() =>
                  navigate('/medico-vfp/pacientes', {
                    state: {
                      focusClave: paciente?.clave,
                      apellido1: paciente?.apellido1 ?? '',
                      apellido2: paciente?.apellido2 ?? '',
                      nombre: paciente?.nombre ?? '',
                    },
                  })
                }
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Volver
              </button>
              <div className="h-5 w-px bg-gray-300"></div>
              <div className="min-w-0 flex items-center gap-2">
                <h1 className="text-sm font-semibold text-gray-900 truncate">
                  {paciente.apellido1} {paciente.apellido2} {paciente.nombre}
                </h1>
                <span className="text-xs text-gray-600 whitespace-nowrap">Clave: {paciente.clave}</span>
              </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-full mx-auto px-2 sm:px-3 lg:px-4 py-2 h-[calc(100vh-56px)]">
        <div className="flex flex-col gap-2 h-full">
          {/* Historial editable */}
          <div className="bg-white rounded-lg shadow flex flex-col flex-[3.4] min-h-[320px]">
            <div className="p-3 flex-1 overflow-auto flex flex-col">
              <div className="text-sm font-semibold text-gray-900 mb-2">Historial (editable)</div>
              <textarea
                rows={14}
                value={historialEditable}
                onChange={(e) => setHistorialEditable(e.target.value)}
                onKeyDown={handleTabKey}
                ref={historialRef}
                style={{ tabSize: 38 }}
                className="w-full h-full min-h-[320px] p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono font-bold text-base leading-snug resize-none"
                placeholder="Sin historial registrado"
              />
            </div>
          </div>

          {/* Datos de la consulta actual (se anexará al historial) */}
          <div className="bg-white rounded-lg shadow flex flex-col flex-[1.1] min-h-[200px]">
            <div className="p-3 flex-1 overflow-hidden flex flex-col">
              <div className="text-sm font-semibold text-gray-900 mb-2">Datos de la consulta actual</div>
              <textarea
                rows={6}
                value={consultaTexto}
                onChange={(e) => setConsultaTexto(e.target.value)}
                onKeyDown={handleTabKey}
                style={{ tabSize: 38 }}
                className="w-full h-full min-h-[200px] p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono font-bold text-base leading-snug resize-none"
              />
            </div>
          </div>

          {/* Solo RECETA con tabs */}
          <div className="bg-white rounded-lg shadow flex flex-col flex-[1.8] min-h-[180px]">
            {/* Tabs */}
            <div className="flex items-center gap-1 px-3 pt-2 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('nueva')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-md transition-colors ${
                  activeTab === 'nueva'
                    ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <FileTextIcon className="w-4 h-4" />
                Receta Nueva
              </button>
              <button
                onClick={() => setActiveTab('previas')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-md transition-colors ${
                  activeTab === 'previas'
                    ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <HistoryIcon className="w-4 h-4" />
                Recetas Previas{recetasPrevias.length > 0 && ` (${recetasPrevias.length})`}
              </button>
              <button
                onClick={() => setActiveTab('orden')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-md transition-colors ${
                  activeTab === 'orden'
                    ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ClipboardListIcon className="w-4 h-4" />
                Orden Ingreso Cirugía
              </button>
            </div>

            {activeTab === 'nueva' ? (
              <div className="p-3 flex-1 overflow-auto flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    RECETA :
                    {editingReceta && (
                      <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                        Editando receta del {formatMexicoCityDate(new Date(editingReceta.fecha))}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      value={recipeFormat}
                      onChange={e => {
                        const val = e.target.value as 'beneficencia' | 'angeles';
                        setRecipeFormat(val);
                        localStorage.setItem('recipeFormat', val);
                      }}
                      className="px-2 py-1 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="beneficencia">Beneficencia Española</option>
                      <option value="angeles">Hospital Ángeles</option>
                    </select>
                    <div
                      className={`text-xs font-semibold px-2 py-1 rounded ${
                      recetaStatus.type === 'error'
                        ? 'bg-red-100 text-red-700'
                        : recetaStatus.type === 'warning'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {recetaStatus.message}
                  </div>
                  </div>
                </div>
                <textarea
                  rows={8}
                  value={recetaTexto}
                  onChange={(e) => setRecetaTexto(e.target.value)}
                  onKeyDown={handleTabKey}
                  style={{ tabSize: 38 }}
                  className={`w-full h-full min-h-[180px] p-3 border rounded-md focus:ring-2 font-mono font-bold text-base leading-snug resize-none ${
                    recetaStatus.type === 'error'
                      ? 'border-red-400 bg-red-50 focus:ring-red-500 focus:border-red-500'
                      : recetaStatus.type === 'warning'
                      ? 'border-yellow-400 bg-yellow-50 focus:ring-yellow-500 focus:border-yellow-500'
                      : 'border-gray-300 focus:ring-purple-500 focus:border-transparent'
                  }`}
                />
              </div>
            ) : activeTab === 'orden' ? (
              <div className="p-3 flex-1 overflow-auto flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-gray-900">ORDEN INGRESO CIRUGIA :</div>
                  <div className="flex items-center gap-3">
                    <select
                      value={ordenFormat}
                      onChange={e => {
                        const val = e.target.value as 'beneficencia' | 'angeles';
                        setOrdenFormat(val);
                        localStorage.setItem('ordenFormat', val);
                      }}
                      className="px-2 py-1 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="beneficencia">Beneficencia Española</option>
                      <option value="angeles">Hospital Ángeles</option>
                    </select>
                    <select
                      value={ordenPlantilla}
                      onChange={e => handlePlantillaChange(e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">Plantilla…</option>
                      <option value="corta-estancia">Plantilla 1 – Corta estancia</option>
                      <option value="pediatria">Plantilla 2 – Pediatría</option>
                    </select>
                    <div
                      className={`text-xs font-semibold px-2 py-1 rounded ${
                      ordenStatus.type === 'error'
                        ? 'bg-red-100 text-red-700'
                        : ordenStatus.type === 'warning'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {ordenStatus.message}
                  </div>
                  </div>
                </div>
                <textarea
                  rows={8}
                  value={ordenTexto}
                  onChange={(e) => setOrdenTexto(e.target.value)}
                  onKeyDown={handleTabKey}
                  style={{ tabSize: 38 }}
                  className={`w-full h-full min-h-[180px] p-3 border rounded-md focus:ring-2 font-mono font-bold text-base leading-snug resize-none ${
                    ordenStatus.type === 'error'
                      ? 'border-red-400 bg-red-50 focus:ring-red-500 focus:border-red-500'
                      : ordenStatus.type === 'warning'
                      ? 'border-yellow-400 bg-yellow-50 focus:ring-yellow-500 focus:border-yellow-500'
                      : 'border-gray-300 focus:ring-purple-500 focus:border-transparent'
                  }`}
                />
              </div>
            ) : (
              <div className="p-3 flex-1 overflow-auto">
                {loadingRecetas ? (
                  <div className="text-center text-gray-500 text-sm py-4">Cargando recetas...</div>
                ) : recetasPrevias.length === 0 ? (
                  <div className="text-center text-gray-400 text-sm py-4">No hay recetas previas</div>
                ) : (
                  <div className="space-y-2">
                    {recetasPrevias.map((receta) => {
                      const fechaReceta = new Date(receta.fecha);
                      const fechaStr = formatMexicoCityDate(fechaReceta);
                      const horaStr = new Intl.DateTimeFormat('es-MX', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      }).format(fechaReceta);
                      const preview = (receta.medicamentos || '').substring(0, 80);
                      return (
                        <div
                          key={receta.id}
                          className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-gray-900">{fechaStr} - {horaStr}</div>
                            <div className="text-xs text-gray-500 truncate mt-0.5">
                              {preview || '(sin medicamentos)'}{receta.medicamentos && receta.medicamentos.length > 80 ? '...' : ''}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const edadParts = receta.edadTexto?.match(/(\d+)\s*años?\s*(\d+)\s*meses?\s*(\d+)\s*d[ií]as?/i) || [];
                              const ageYears = edadParts[1] || '';
                              const ageMonths = edadParts[2] || '';
                              const ageDays = edadParts[3] || '';
                              const fechaReceta = new Date(receta.fecha);
                              navigate(`/medico-vfp/${paciente.clave}/receta/print`, {
                                state: {
                                  prescription: receta.medicamentos || '',
                                  weight: receta.peso != null ? String(receta.peso) : '',
                                  height: receta.talla != null ? String(receta.talla) : '',
                                  ageYears,
                                  ageMonths,
                                  ageDays,
                                  date: formatMexicoCityDate(fechaReceta),
                                  weightPercentile: receta.percentilePeso || '',
                                  heightPercentile: receta.percentileTalla || '',
                                  recetaToken: receta.token,
                                  formato: receta.formato || 'beneficencia',
                                },
                              });
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 transition-colors ml-2"
                          >
                            <RotateCwIcon className="w-3.5 h-3.5" />
                            Reimprimir
                          </button>
                          <button
                            onClick={() => handleEditarReceta(receta)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors ml-1"
                          >
                            <PencilIcon className="w-3.5 h-3.5" />
                            Editar
                          </button>
                          <button
                            onClick={() => handleBorrarReceta(receta.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors ml-1"
                          >
                            <Trash2Icon className="w-3.5 h-3.5" />
                            Borrar
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Botones de acción al pie, alineados a la derecha */}
          <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-gray-200 bg-white rounded-lg shadow print:hidden">
            {activeTab === 'orden' ? (
              <button
                onClick={handleImprimirOrden}
                className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
              >
                <PrinterIcon className="w-4 h-4" />
                Imprimir Orden
              </button>
            ) : editingReceta ? (
              <>
                <button
                  onClick={handleCancelarEdicion}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm"
                >
                  <XIcon className="w-4 h-4" />
                  Cancelar
                </button>
                <button
                  onClick={() => handleGuardarEdicion(false)}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                >
                  <SaveIcon className="w-4 h-4" />
                  Guardar cambios
                </button>
                <button
                  onClick={() => handleGuardarEdicion(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
                >
                  <PrinterIcon className="w-4 h-4" />
                  Guardar e imprimir
                </button>
              </>
            ) : (
            <button
              onClick={handleGuardarReceta}
              className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
            >
              <PrinterIcon className="w-4 h-4" />
              Imprimir Receta
            </button>
            )}
            <button
              onClick={() =>
                navigate('/medico-vfp/pacientes', {
                  state: {
                    focusClave: paciente?.clave,
                    apellido1: paciente?.apellido1 ?? '',
                    apellido2: paciente?.apellido2 ?? '',
                    nombre: paciente?.nombre ?? '',
                  },
                })
              }
              className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
            >
              Cerrar Ventana
            </button>
          </div>
        </div>
      </div>

      {/* Versión para impresión */}
      {/* Sin impresión directa desde esta pantalla */}
    </div>
  );
}
